import { QuestionAnswerMap } from '@care-portals/api-interfaces';
import { IMAGE_TYPES } from '../../../../constants';

const facialAntiAgingQuestions = [
  {
    label: 'What was your sex assigned at birth?',
    field: 'facialAntiAging_sexAssignedAtBirth',
    extras: { sendToBeluga: false },
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { key: 'male', value: 'Male' },
        { key: 'female', value: 'Female' }
      ]
    }
  },
  {
    label:
      'Are you currently pregnant, breastfeeding or planning to become pregnant?',
    field: 'facialAntiAging_pregnancy',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { key: 'yes', value: 'Yes' },
        { key: 'no', value: 'No' }
      ]
    }
  },
  {
    label: 'Consent (pregnancy)',
    field: 'facialAntiAging_pregnancyConsent',
    type: 'select',
    educationalSlide: `I understand that the medication prescribed to me by my healthcare provider may not be safe to take during pregnancy. I acknowledge that taking this medication while pregnant could pose risks to my health and the health of a developing fetus.

I agree to take necessary precautions to avoid becoming pregnant while using this medication, including the use of effective contraception methods as discussed with my healthcare provider.

I understand that I should stop taking this medication before attempting to become pregnant. I agree to consult with my healthcare provider prior to discontinuing the medication and before planning a pregnancy to ensure my safety and well-being.

By selecting below, I confirm that I have read and understand the information provided above. I consent to proceed with the treatment under these conditions.`,
    answer: {
      type: 'single_select',
      options: [
        {
          class: 'option-decline',
          key: 'non_consent',
          value: 'I have read the information and do NOT wish to proceed'
        },
        {
          class: 'option-consent',
          key: 'consent',
          value:
            'I have read and understand the above information, I understand the risks and wish to proceed'
        }
      ]
    }
  },
  {
    label:
      'To help us customize your treatment, please tell us which areas you would like to focus on.',
    subLabel: 'Select all that apply',
    field: 'facialAntiAging_focusAreas',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        {
          key: 'face_neck',
          value: 'I have concerns with the skin of my face and/or neck'
        },
        {
          key: 'lips_perioral',
          value: 'I have concerns with my lips or around the mouth'
        },
        {
          key: 'undereye_area',
          value: 'I have concerns with my undereye area (bags and/or puffiness)'
        }
      ]
    }
  },
  {
    label:
      'We want to understand your top priorities for your skin. Which goals matter most to you?',
    subLabel: 'Select all that apply',
    field: 'facialAntiAging_goals',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        { key: 'goal_wrinkles', value: 'Smooth wrinkles' },
        { key: 'goal_dark_spots', value: 'Reduce age or dark spots' },
        {
          key: 'goal_blemish',
          value: 'Reduce blemishes, minimize pores and/or treat pimples'
        },
        { key: 'goal_redness', value: 'Calm redness' },
        {
          key: 'goal_firmness',
          value: 'Improve overall firmness/bounce'
        },
        {
          key: 'goal_undereye',
          value: 'Reduce puffiness or dark circles under my eyes'
        }
      ]
    }
  },
  {
    label: 'Where are your lines/wrinkles?',
    field: 'facialAntiAging_wrinklesLocations',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        {
          key: 'forehead',
          value: 'Forehead lines – the horizontal lines across your forehead'
        },
        {
          key: 'elevens',
          value: '11’s – the vertical lines between your eyebrows'
        },
        {
          key: 'crows_feet',
          value: 'Crow’s feet – the fine lines at the corners of your eyes'
        },
        {
          key: 'under_eye_crepe',
          value: 'Under-eye crepe – the thin, crepey skin just under the eyes'
        },
        {
          key: 'lip_lines',
          value: 'Lip lines – fine lines that appear around the mouth'
        },
        {
          key: 'neck_bands',
          value:
            'Neck bands – the visible lines or cords that can show across or down the neck'
        }
      ]
    }
  },
  {
    label: 'Which best describes your dark spots?',
    field: 'facialAntiAging_darkSpots',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        {
          key: 'sun_spots',
          value:
            'Scattered sun spots – flat, brown spots from sun exposure (often on the face, chest, or hands)'
        },
        {
          key: 'blotchy',
          value:
            'Blotchy patches – uneven darker areas, often on the cheeks, upper lip, or forehead'
        },
        {
          key: 'pih',
          value:
            'Post-blemish marks (PIH) – dark marks left behind after acne or other skin irritation'
        }
      ]
    }
  },
  {
    label:
      'What best describes the severity of your blemishes, pores, and/or pimples?',
    field: 'facialAntiAging_blemishSeverity',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { key: 'mild', value: 'Mild' },
        { key: 'moderate', value: 'Moderate' },
        { key: 'severe', value: 'Severe' }
      ]
    }
  },
  {
    label: 'Which redness/texture concerns apply?',

    field: 'facialAntiAging_rednessConcerns',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        { key: 'central_redness', value: 'Persistent central redness' },
        { key: 'flushing', value: 'Flushing' },
        { key: 'rough_texture', value: 'Rough / uneven texture' }
      ]
    }
  },
  {
    label:
      'We’d like to know more about your goals for firmness and bounce. Which of the following best describes what you would like to improve?',

    field: 'facialAntiAging_firmnessGoals',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        {
          key: 'less_firm',
          value: 'My skin feels less firm or saggy'
        },
        {
          key: 'crepe_texture',
          value: 'I notice fine lines or crepe-like texture from thinning skin'
        },
        {
          key: 'plump_hydration',
          value: 'I want more plumpness and hydration for a “bouncy” feel'
        },
        {
          key: 'neck_jaw_loose',
          value: 'I want to reduce the look of loose skin in my neck or jawline'
        },
        {
          key: 'elasticity',
          value:
            'I’d like to improve the elasticity and overall tightness of my skin'
        }
      ]
    }
  },
  {
    label: 'What concerns do you have about your undereye area?',

    field: 'facialAntiAging_undereyeConcerns',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        { key: 'puffiness', value: 'Puffiness or swelling' },
        { key: 'dark_circles', value: 'Dark circles' },
        {
          key: 'fine_lines',
          value: 'Fine lines or crepe-like skin'
        },
        { key: 'irritation', value: 'Irritation or sensitivity' },
        { key: 'hollowing', value: 'Hollowing or loss of volume' }
      ]
    }
  },
  {
    label: 'What is your skin type?',
    field: 'facialAntiAging_skinType',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { key: 'dry', value: 'Dry' },
        { key: 'oily', value: 'Oily' },
        {
          key: 'neither',
          value: 'I do not have issues with either dry or oily skin'
        }
      ]
    }
  },
  {
    label: 'Do you have any of the following?',
    subLabel: 'Select all that apply',
    field: 'facialAntiAging_acuteConcerns',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        {
          key: 'changing_lesion',
          value:
            'I have rapidly growing or changing dark lesion, bleeding or non‑healing sore, or new severe facial swelling/pain'
        },
        {
          key: 'vision_eye',
          value: 'I have sudden vision change, eye pain, or undereye mass'
        },
        {
          key: 'active_infection',
          value:
            'I have an active facial infection (impetigo, cellulitis), shingles, or severe cold sore outbreak today'
        },
        {
          key: 'photosensitizing',
          value:
            'I have a history of strong reactions to photosensitizing medications which cause severe and serious sun burns'
        },
        {
          key: 'blood_clots',
          value:
            'I have a history of blood clots (DVT) or pulmonary embolisms (PE)'
        },
        {
          key: 'estrogen_cancer',
          value:
            'I have a history of an estrogen sensitive cancer such as breast, ovarian, and uterine cancer'
        },
        {
          key: 'none_of_the_above',
          value: 'I do not have any of the above',
          uncheckOthers: true
        }
      ]
    }
  },
  {
    label:
      'Which product(s) have you had allergic reactions or side effects to in the past?',
    subLabel: 'Select all that apply',
    field: 'facialAntiAging_productReactions',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        {
          key: 'retinoid_irritation',
          value: 'Retinoid irritation (severe)'
        },
        {
          key: 'vitamin_c_sting',
          value: 'Vitamin C sting/burning'
        },
        { key: 'aha_peel', value: 'AHA peel/burn' },
        { key: 'sulfur_irritation', value: 'Sulfur irritation' },
        {
          key: 'niacinamide_flush',
          value: 'Niacinamide flush/itch'
        },
        {
          key: 'no_reactions',
          value: 'I have never used or never had reactions to the above',
          uncheckOthers: true
        }
      ]
    }
  },
  {
    label: 'Program information',
    field: 'facialAntiAging_programEducationConsent',
    type: 'select',
    educationalSlide: `In order to help make you successful in this program, please read the following information:

To help your skin adjust safely and reduce the chance of irritation:
• Patch test new products first: Apply a small amount to the jawline or behind the ear and monitor for 24–48 hours before using it on larger areas. Watch for redness, itching, or rash. If irritation occurs, stop and let your provider know.
• Start slowly: Use new actives 2–3 nights per week and increase gradually (every other night → nightly) only as tolerated.
• Use the right amounts: A pea-sized amount for the full face, a grain-of-rice for each undereye, and a nickel- to dime-sized amount for the neck/chest.
• Pause use after procedures, during sunburn or windburn, with active cold sores, open wounds, pregnancy/breastfeeding, or if you develop moderate to severe irritation that doesn’t improve with spacing out use and adding moisturizer.

Sun protection is essential:
• Apply broad-spectrum SPF ≥30 every morning to the face, neck, and chest, and reapply with sun exposure.
• Wear hats and sunglasses, seek shade when possible, and avoid intentional tanning.
• If you have irritation or healing skin, keep the area covered with clothing, hats, or scarves until it is calm.

Undereye guidance:
• If you’re using an undereye formula, only use formulas made for the eye area, apply a grain-of-rice amount, and avoid the lash line and inner corners as these are sensitive areas.`,
    answer: {
      type: 'single_select',
      options: [
        {
          class: 'option-decline',
          key: 'non_consent',
          value:
            'I have read the above information and I do not wish to continue'
        },
        {
          class: 'option-consent',
          key: 'consent',
          value:
            'I have read the above information and I do consent and wish to move forward'
        }
      ]
    }
  },
  {
    label: 'What other information or questions do you have for the doctor?',
    field: 'facialAntiAging_otherInformation',
    type: 'info',
    answer: {
      name: 'facialAntiAging_otherInformation',
      type: 'text',
      rows: 4,
      placeholder: 'Optional details or questions'
    }
  },

  {
    label: 'Consent (Truthfulness)',
    field: 'facialAntiAging_truthConsent',
    type: 'select',
    educationalSlide: `Please attest to the following confirming that all information you have provided to us is true and complete.

I verify that I am the patient and that I have answered the questions asked in this intake form. I confirm that I have reviewed and understood all the questions asked of me. I attest that the answers and information I have provided in this questionnaire is true and complete to the best of my knowledge. I understand that it is critical to my health to share complete health information with my doctor. I will not hold the doctor or affiliated medical practice responsible for any oversights or omissions, whether intentional or not, in the information that I provided.`,
    answer: {
      type: 'single_select',
      options: [
        {
          class: 'option-decline',
          key: 'non_consent',
          value:
            'I have read the above information and I do NOT wish to continue'
        },
        {
          class: 'option-consent',
          key: 'consent',
          value:
            'I have read the above information and I do consent and wish to move forward'
        }
      ]
    }
  },
  {
    label: 'Consent (Medication Specific)',
    field: 'facialAntiAging_medicationConsent',
    type: 'select',
    educationalSlide: `You are requesting evaluation and treatment with topical dermatologic therapies for cosmetic skin
concerns of the face, undereyes, lips/perioral area, neck, and/or decolletage. Treatment may include
custom compounded or commercially available products such as the below products or combinations of
these products.:

- Retinoids (tretinoin, adapalene, retinol)
- Peptides/growth‑factor topicals (e.g., Argireline, GHK-Cu, etc.)
- Topical estriol/estrogen (low dose) for fine lines/skin thinning (when clinically appropriate)
- Antioxidants (Vitamin C, Vitamin E, CoQ10), niacinamide
- Hyaluronic acid (HA) and HA + peptide combinations
- NAD+ or resveratrol topicals

Azelaic acid, sulfur, lactic/glycolic acid (AHAs)
Specialty items (e.g., caffeine under‑eye gels, DNA‑repair enzyme and aloe‑based post‑sun
formulations)
These therapies aim to improve photoaging and texture through exfoliation, collagen support, pigment
modulation, oil control, and barrier optimization.

Potential Benefits

-Smoother skin texture; reduction in fine lines and wrinkles
-More even tone; improvement of dark spots/melasma/PIH
- Improved firmness/elasticity and overall radiance
-Pore appearance refinement and oil/blemish control
-Support for skin barrier and hydration

Individual results vary. Multiple weeks of consistent use and sun protection are typically
required.

Potential Side Effects
While these treatments are commonly well tolerated, they may cause side effects. In rare cases, reactions
can be severe and may require in‑person evaluation or urgent care.
Common side effects (usually temporary):

- Dryness, tightness, peeling/flaking, mild redness or burning/tingling after application
- Purging (temporary increase in breakouts) with retinoids/azelaic acid
- Stinging with Vitamin C; flush/itch with niacinamide; dryness/odor with sulfur
- Transient photosensitivity (greater sunburn risk), especially with retinoids/AHAs
- Under‑eye dryness/irritation if products are applied too close to the lid margin

Less common but potentially serious:

- Severe irritation/dermatitis or blistering; chemical burn with overuse/misuse
- Eye injury or persistent eye irritation if product enters the eye
- Secondary infection of irritated or broken skin
- Post‑inflammatory hyperpigmentation or hypopigmentation, more likely in darker skin tones after inflammation
- Allergic contact dermatitis to an active or excipient (e.g., fragrance/preservatives)
- Cold sore (HSV‑1) flares triggered by irritation or sun exposure (if you have a history of HSV‑1)

Estriol/estrogen‑containing topicals (if used):

- Local irritation; theoretical systemic hormone effects are unlikely but possible
- Do not use if pregnant, breastfeeding, trying to conceive, or with a history of estrogen‑dependent cancer, blood clots (DVT/PE), stroke, severe liver disease, or migraine with aura, unless specifically cleared by your clinician.

Risks and Considerations

- Pregnancy/Breastfeeding/Trying to Conceive: Do not use retinoids or estriol. Notify us immediately if your status changes; stop these products.
- Photosensitivity: Retinoids and AHAs increase sun sensitivity-daily SPF ≥30 is required.
- Sensitive skin/rosacea/eczema/perioral dermatitis: You may need a barrier‑first approach and slower ramp; stronger acids may be limited.
- Darker skin tones (Fitzpatrick IV-VI): Higher risk of pigment changes after irritation-go low and slow and avoid sunburn.
- Recent procedures (peels/laser/microneedling/waxing): You may need to pause use 5–7 days after the procedure or as directed.
- Eye & mucosal safety: Avoid eyelids, lash line, and lips unless a product is designed for that area. Rinse thoroughly if contact occurs.
- HSV‑1 history: Irritation and sun may trigger flares; pause use during an outbreak and contact your provider.
- Allergies: Patch testing is recommended. Disclose all skincare/medication allergies.
- Off‑label/compounded use: Some formulations/uses are off‑label and compounded, which are not FDA‑approved products for cosmetic indications. Quality standards are followed by the dispensing pharmacy.
- Not a skin cancer screening: New or changing lesions require in‑person dermatology evaluation.

Monitoring and Follow‑up

- We may request date‑stamped facial/neck photos to monitor response and treatment consistency.
- Your regimen may be adjusted (ingredients, strengths, frequency) based on results or side effects. 
- Seek urgent, in‑person care for: rapidly changing dark lesion, severe facial swelling, widespread blistering rash, eye pain/vision changes after exposure, signs of infection (fever, pus), or breathing difficulty.

Sun protection:

- Apply broad‑spectrum SPF ≥30 every morning and reapply with exposure.
- Wear hats/seek shade; avoid intentional tanning.

Acknowledgment and Consent

By signing/checking below, you acknowledge and agree that:

- I have reviewed the potential benefits, risks, and side effects of topical anti‑aging treatments listed above.
- I understand that retinoids and estriol must not be used in pregnancy, breastfeeding, or while trying to conceive, and I will notify my provider immediately if my status changes.
- I understand the photosensitivity risk and the requirement for daily SPF ≥30.
- I understand some therapies may be off‑label and/or compounded and still consent to their use as recommended.
- I consent to telemedicine care, including secure photo sharing for clinical documentation and treatment monitoring.

- I agree to attend follow‑up appointments and communicate any side effects promptly.
`,
    answer: {
      type: 'single_select',
      options: [
        {
          class: 'option-decline',
          key: 'non_consent',
          value:
            'I have read the above information and I do NOT wish to continue'
        },
        {
          class: 'option-consent',
          key: 'consent',
          value:
            'I have read the above information and I do consent and wish to move forward'
        }
      ]
    }
  },
  {
    field: 'facialAntiAging_complete',
    isLast: true,
    label:
      'Thank you for completing this questionnaire, please select "Finish" to submit',
    extras: { sendToBeluga: false },
    type: 'info',
    answer: {}
  },
  {
    class: 'ineligible',
    field: 'facialAntiAging_ineligible',
    label: "We're sorry you don't qualify",
    subLabel: "We're not able to prescribe for you today",
    type: 'info',
    isLast: true,
    blockQuestionFlow: true,
    answer: {},
    extras: { sendToBeluga: false }
  }
] as const;

export type FacialAntiAgingQuestionFieldAnswers = Partial<
  QuestionAnswerMap<typeof facialAntiAgingQuestions>
>;
export default facialAntiAgingQuestions;
